// Color palette names follow design system tokens.
// Off-palette colors (pending design decision) are grouped at the bottom — see GH issue #23244.
//
// POC #33148: values swapped to @ourbranch/ui-core's palette (packages/ui-core/src/variables.ts).
// Token NAMES are unchanged so the 407 style files keep working; only the hex values moved.
// Numeric suffixes no longer imply lightness, and some pairs now share a ui-core value.
// Hex is copied rather than imported because ui-core/src/utils.ts pulls MUI v5 in at module
// scope, which would drag Emotion into staff-fe's v4 bundle. See the proposal's "Palette sync".
export const colorPalette = {
  // ---- Light ---- (ui-core white50 / beige50 / beige100 / beige150 / beige200 / beige700 / beige900)
  white: '#FFFFFF',
  white_op_10: 'rgba(255, 255, 255, 0.10)',
  white_op_15: 'rgba(255, 255, 255, 0.15)',
  white_op_30: 'rgba(255, 255, 255, 0.3)',
  beige_25: '#F8F6F2',
  beige_25_op_30: 'rgba(248, 246, 242, 0.3)',
  beige_40: '#F3F0ED',
  beige_50: '#F1ECE8',
  beige_50_op_13: 'rgba(241, 236, 232, 0.13)',
  beige_50_op_50: 'rgba(241, 236, 232, 0.5)',
  beige_50_op_60: 'rgba(241, 236, 232, 0.60)',
  beige_50_op_70: 'rgba(241, 236, 232, 0.7)',
  beige_50_op_75: 'rgba(241, 236, 232, 0.75)',
  beige_60: '#DFD8D2',
  beige_75: '#B4AB99',
  beige_90: '#706553',

  // ---- Dark (Green) ---- (ui-core green100 / green150 / green200 / green600)
  green_50: '#527059',
  green_75: '#527059',
  green_75_op_10: 'rgba(82, 112, 89, 0.10)',
  green_75_op_30: 'rgba(82, 112, 89, 0.3)',
  green_75_op_40: 'rgba(82, 112, 89, 0.4)',
  green_75_op_60: 'rgba(82, 112, 89, 0.60)',
  green_75_op_75: 'rgba(82, 112, 89, 0.75)',
  green_75_10: '#EDF0EE',
  green_80: '#343B1B',
  green_90: '#343B1B',
  green_90_op_80: 'rgba(52, 59, 27, 0.8)',
  green_100: '#0B1F15',

  // ---- Accent (Bark) ---- (ui-core bark500 / bark100 / bark50)
  orange_50: '#C93800',
  orange_50_op_08: 'rgba(201, 56, 0, 0.08)',
  orange_50_op_10: 'rgba(201, 56, 0, 0.1)',
  orange_50_op_50: 'rgba(201, 56, 0, 0.5)',
  orange_50_op_70: 'rgba(201, 56, 0, 0.7)',
  orange_75: '#EF4E10',
  // ui-core bark50. Accent text on dark-green surfaces: bark500 only reaches 2.27:1 there,
  // where the old amber passed. Use this wherever the accent sits on green_75/green_90/green_100.
  orange_on_dark: '#FFCEBC',

  // ---- Error (Red) ---- (ui-core red50 / red10 / red100)
  red_50: '#7A0000',
  red_50_op_10: 'rgba(122, 0, 0, 0.1)',
  red_100: '#7A0000',
  red_100_10: '#FBEBE9',

  // ---- Off-palette (pending design decision; see #23244) ----
  green_legacy_86c440: '#527059',
  grey_10_op_38: 'rgba(52, 59, 27, 0.38)',
  orange_40: 'rgba(201, 56, 0, 0.4)',

  // ---- Folded in from the semantic `colors` map (POC #33148) ----
  // These previously bypassed the palette as raw hex literals in `colors` below.
  green_success: '#527059',
  green_success_light: '#B4C7B8',
  green_success_dark: '#0B1F15',
  green_success_dark_op_90: 'rgba(11, 31, 21, 0.9)',
  red_error: '#7A0000',
  red_error_light: '#FBEBE9',
  red_error_dark: '#3D0202',
  red_error_dark_op_90: 'rgba(61, 2, 2, 0.9)',
  black: '#000000',
  black_op_87: 'rgba(0, 0, 0, 0.87)',
  black_op_70: 'rgba(0, 0, 0, 0.70)',
  gray_page: '#F8F6F2',
  gray_01: '#F3F0ED',
  gray_02: '#F8F6F2',
  gray_03: '#757774',
  gray_04: '#343B1B'
};

export const colors = {
  green: colorPalette.green_success,
  green_light: colorPalette.green_success_light,
  green_80: colorPalette.green_80,
  green_dark: colorPalette.green_success_dark,
  green_dark_90: colorPalette.green_success_dark_op_90,
  red: colorPalette.red_error,
  red_light: colorPalette.red_error_light,
  red_dark: colorPalette.red_error_dark,
  red_dark_90: colorPalette.red_error_dark_op_90,
  black: colorPalette.black,
  gray: colorPalette.gray_page,
  white: colorPalette.white,
  gray_01: colorPalette.gray_01,
  gray_02: colorPalette.gray_02,
  gray_03: colorPalette.gray_03,
  gray_04: colorPalette.gray_04,

  // page general styles
  page__title: colorPalette.green_75,
  page__subtitle: colorPalette.green_80,
  page__subtitle1: colorPalette.green_90,
  page__background_primary: colorPalette.beige_50,
  // buttons
  button__background_primary: colorPalette.green_100,
  button__background_secondary: colorPalette.orange_50,
  button__text_primary: colorPalette.white,

  // header
  header__background_primary: colorPalette.beige_50,
  header__label_primary: colorPalette.green_90,
  header__label_secondary: colorPalette.green_75,

  // footer
  footer_background_primary: colorPalette.beige_50,
  // card styles
  info__background_primary: colorPalette.white,
  info__label_primary: colorPalette.green_80,
  info__label_secondary: colorPalette.green_75,
  info__label_title: colorPalette.green_75,
  info__label_subtitle: colorPalette.green_75,
  info__label_tertiary: colorPalette.orange_75,
  info__label_text: colorPalette.orange_50,

  coverage__background_primary: colorPalette.green_90,
  coverage__background_secondary: colorPalette.green_75,
  coverage__label_primary: colorPalette.beige_50,
  coverage__label_secondary: colorPalette.beige_50_op_75,
  coverage__label_subtitle: colorPalette.beige_50,
  coverage__label_title: colorPalette.beige_50,
  coverage__label_paragraph: colorPalette.beige_50_op_75,

  payment__background_primary: colorPalette.green_75,
  payment__background_secondary: colorPalette.beige_25,
  terms__background_primary: colorPalette.beige_25,

  card_primary_separator: colorPalette.beige_25,

  // form styles
  form__background_primary: colorPalette.beige_25,
  form_label_primary: colorPalette.beige_50_op_70,
  // plan picker styles
  planPicker_label_primary: colorPalette.beige_50,
  planPicker_label_secondary: colorPalette.green_75,

  text_primary: colorPalette.green_90,
  text_secondary: colorPalette.green_75,
  text_disabled: colorPalette.grey_10_op_38,
  text_secondary_background: colorPalette.white,

  light_text: colorPalette.beige_50,
  medium_text: colorPalette.beige_60,
  light_background: colorPalette.white_op_10,

  card_background_primary: colorPalette.white,
  card_background_primary_editable: colorPalette.beige_25,
  card_background_secondary: colorPalette.green_90,
  card_background_tertiary: colorPalette.orange_75,
  card_background_modal: colorPalette.beige_50,

  loading_background: colorPalette.orange_40,
  loading_bar: colorPalette.orange_50,

  body: colorPalette.black_op_87,
  body_light: colorPalette.black_op_70,
  error: colorPalette.red_50,
  success: colorPalette.green_legacy_86c440
};

export const screens = {
  mobile: 500,
  tablet: 768,
  small_desktop: 1024,
  standard_desktop: 1280,
  medium_desktop: 1400,
  big_desktop: 1680
};

export const sizes = {
  headerHeight: 96,
  footerHeight: 100,
  sideColumn: 360,
  contentColumn: 744,
  roundCorners: 6
};

export const fonts = {
  // The `!important` makes sure that using a font explicitly
  // will override any font being inherited from higher up in the tree
  // (which will also be !important), but cannot be overridden by any
  // fonts coming from Material UI. (Ex: if you remove the !important,
  // tooltips from Material UI won't use the font you specify for the
  // selector '*')
  //
  // POC #33148: both slots now resolve to Cabin, matching ui-core. The
  // FrankRuhlLibre key is kept so its one caller (error-dialog) still resolves.
  FrankRuhlLibre: `'Cabin', -apple-system, BlinkMacSystemFont, Segoe UI, Helvetica, Arial, sans-serif !important`,
  LibreFranklin: `'Cabin', -apple-system, BlinkMacSystemFont, Segoe UI, Helvetica, Arial, sans-serif !important`
};
